import { DialogButton } from "@constellation-academy/framework-react-components";
import { useSelector } from "react-redux";
import { useMutation } from "react-relay";
import { getCertificationDataObject } from "@features/licenses/create-licenses-button/create-licenses-button.utils";
import { useTranslation } from "@i18n/hooks";
import { type createLicensesButton_CreateLicensesMutation } from "@relay/createLicensesButton_CreateLicensesMutation.graphql";
import { selectActiveFeatureToggles } from "@store/slices/FeatureTogglesSlice";
import { CREATE_LICENSES_MUTATION } from "./create-licenses-button.graphql";
import { type CreateLicensesButtonProps } from "./create-licenses-button.types";
import { CreateLicensesForm } from "../create-licenses-form";
import { type CreateLicensesFormState } from "../create-licenses-form/create-licenses-form.types";

export const CreateLicensesButton = ({
	accountId,
	connectionId,
	onLicenseCreated,
}: CreateLicensesButtonProps) => {
	const { t } = useTranslation("common");

	const activeFeatureToggles = useSelector(selectActiveFeatureToggles);
	const hasSplitCourseCertification = activeFeatureToggles?.includes("splitCourseCertification");

	const [createLicenses, isCreatingLicenses] =
		useMutation<createLicensesButton_CreateLicensesMutation>(CREATE_LICENSES_MUTATION);

	const handleOnSubmit = (values: CreateLicensesFormState, hide: () => void) => {
		if (!values.licenseDefinition || !accountId) {
			return;
		}
		const certificationData = hasSplitCourseCertification
			? getCertificationDataObject(values.certificationDataType || "WithCertification")
			: getCertificationDataObject("WithCertification");

		createLicenses({
			variables: {
				input: {
					amount: values.amount,
					licenseDefinitionId: values.licenseDefinition.id,
					accountId,
					certificationData,
				},
				connections: [connectionId!],
			},
			onCompleted: () => {
				hide();
				onLicenseCreated?.();
			},
		});
	};

	return (
		<>
			<DialogButton
				buttonContent={{ label: t("license.addLicenses") }}
				title={t("license.addLicenses")}
				disabled={isCreatingLicenses}
			>
				{(hide) => (
					<CreateLicensesForm onSubmit={(values) => handleOnSubmit(values, hide)} />
				)}
			</DialogButton>
		</>
	);
};
